
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { BeforeDestroyAware, CreatedAware } from '@/util/lifecycle';
import ToolbarInnerScaffold from './ToolbarInnerScaffold.vue';
import BaseCrudRepository, { Pageable, Sort, SortDirection } from '@/repositories/BaseCrudRepository';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import Page from '@/repositories/data/Page';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import PositionableCrudRepository from '@/repositories/PositionableCrudRepository';

interface Options {
    page: number;
    itemsPerPage: number;
    sortBy: string[];
    sortDesc: boolean[];
}

function createDefaultOptions(): Options {
    return {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
    };
}

@Component({
    components: {
        ToolbarInnerScaffold,
    },
})
export default class PagedDataTable extends Vue implements CreatedAware, BeforeDestroyAware {
    public loading = true;
    public search = '';
    public latestError: string | null = null;
    public displayError = false;
    public deleteDialog = false;
    public deleting = false;
    public tableOptions: Options = createDefaultOptions();
    public footerProps = {
        itemsPerPageOptions: [20, 50, 100],
    };

    @Prop({ type: Boolean, required: false, default: true })
    public readonly deletable!: boolean;

    @Prop({ type: Boolean, required: false, default: true })
    public readonly editable!: boolean;

    @Prop({ type: String, required: true })
    private readonly i18nGroup!: string;

    @Prop({ type: String, required: true })
    private readonly formRoute!: string;

    @Prop({ type: Array, required: true })
    private readonly headers!: any[];

    @Prop({ required: true, type: Object })
    private readonly repository!: BaseCrudRepository<any, any, any>;

    @Prop({ type: Boolean, required: false, default: false })
    private readonly enableSearch!: boolean;

    // noinspection JSMismatchedCollectionQueryUpdate
    @Prop({ required: false, type: Array, default: () => [] })
    private readonly initialSort!: Sort[];

    private deleteTarget: any = null;
    private deleteSubscription = new Subscription();
    private moveSubscription = new Subscription();
    private tableOptionsSubject = new BehaviorSubject<Options>(this.tableOptions);
    private searchSubject = new BehaviorSubject<string>('');
    private pageSubscription = new Subscription();
    private pageSubject = new BehaviorSubject<Page<any>>({
        content: [],
        total: 0,
        pageable: {
            page: 0,
            size: 1,
        },
    });

    public get items(): any[] {
        return this.pageSubject.value.content;
    }

    public get itemsTotal(): number {
        return this.pageSubject.value.total;
    }

    public get isPositionable(): boolean {
        return PositionableCrudRepository.isInstance(this.repository);
    }

    public created(): void {
        this.updateFromRouteQuery();
        if (this.initialSort.length > 0) {
            for (const sort of this.initialSort) {
                this.tableOptions.sortBy.push(sort.property);
                this.tableOptions.sortDesc.push(sort.direction === SortDirection.DESC);
            }
        }
        this.pageSubscription = combineLatest([this.tableOptionsSubject, this.searchSubject.pipe(debounceTime(300))])
            .pipe(
                map(
                    ([options, search]): Pageable => ({
                        page: options.page - 1,
                        size: options.itemsPerPage,
                        sort: options.sortBy.map((t, i) => ({
                            property: t,
                            direction: options.sortDesc[i] ? SortDirection.DESC : SortDirection.ASC,
                        })),
                        filter: search,
                    }),
                ),
                switchMap((pageable) => this.repository.findPage(pageable)),
            )
            .subscribe((pageResource) => {
                this.loading = pageResource.isLoading;
                this.latestError = this.extractErrorMessage(pageResource.error);
                this.displayError = this.displayError || pageResource.isError;
                if (pageResource.isSuccess && pageResource.data) {
                    this.pageSubject.next(pageResource.data);
                } else if (pageResource.isError) {
                    this.$emit('error', pageResource.error);
                }
            });
    }

    public beforeDestroy(): void {
        this.deleteSubscription.unsubscribe();
        this.pageSubscription.unsubscribe();
        this.moveSubscription.unsubscribe();
    }

    @Watch('tableOptions', { deep: true })
    private onTableOptionsChanged(options: Options): void {
        this.tableOptionsSubject.next(options);
        this.mergeRouteQuery({
            pi: options.page.toString(10),
            ps: options.itemsPerPage.toString(10),
        });
    }

    @Watch('search', { deep: true })
    private onSearchChanged(search: string): void {
        this.searchSubject.next(search);
    }

    @Watch('$route')
    public onRouteChanged() {
        this.updateFromRouteQuery();
    }

    public onNew(): void {
        this.$router.push({ name: this.formRoute, params: { id: 'new' } });
    }

    public editItem(item: any): void {
        this.$router.push({ name: this.formRoute, params: { id: item.id } });
    }

    public deleteItem(item: any): void {
        this.deleteTarget = item;
        this.deleteDialog = true;
    }

    public moveItem(item: any, up: boolean): void {
        if (PositionableCrudRepository.isInstance(this.repository)) {
            this.moveSubscription.unsubscribe();
            this.moveSubscription = this.repository.movePosition(item.id, up).subscribe(() => {
                this.tableOptionsSubject.next(this.tableOptions);
            });
        }
    }

    public confirmDelete(): void {
        if (this.deleteTarget) {
            this.deleteSubscription.unsubscribe();
            this.deleting = true;
            this.deleteSubscription = this.repository.delete(this.deleteTarget.id).subscribe((resource) => {
                this.deleting = resource.isLoading;
                this.latestError = this.extractErrorMessage(resource.error);
                this.displayError = this.displayError || resource.isError;
                this.deleteDialog = false;
                this.tableOptionsSubject.next(this.tableOptions);
            });
        }
    }

    public cancelDelete(): void {
        this.deleteSubscription.unsubscribe();
        this.deleting = false;
        this.deleteDialog = false;
        this.deleteTarget = null;
    }

    public submit(): void {
        this.$emit('search', this.search);
    }

    private updateFromRouteQuery() {
        if (this.$route.query.hasOwnProperty('pi')) {
            this.tableOptions.page =
                typeof this.$route.query.pi === 'string' && /^[-+]?(\d+)$/.test(this.$route.query.pi)
                    ? parseInt(this.$route.query.pi)
                    : 1;
        }
        if (this.$route.query.hasOwnProperty('ps')) {
            this.tableOptions.itemsPerPage =
                typeof this.$route.query.ps === 'string' && /^[-+]?(\d+)$/.test(this.$route.query.ps)
                    ? parseInt(this.$route.query.ps)
                    : 20;
        }
    }

    private mergeRouteQuery(query: any) {
        const nextQuery = {
            ...this.$route.query,
            ...query,
        };
        Object.keys(query).forEach((key) => {
            if (query[key] === null) {
                delete nextQuery[key];
            }
        });
        this.$router
            .replace({
                ...this.$router.currentRoute,
                name: this.$router.currentRoute.name!,
                query: nextQuery,
            })
            .catch(() => {
                // ignored
            });
    }

    private extractErrorMessage(error: any): string | null {
        const currentMessage = this.displayError ? this.latestError : null;
        if (error && error.response) {
            return error.response.status === 409 ? this.$t('general.conflictError').toString() : currentMessage;
        }
        return currentMessage;
    }
}
